/**
 * Unified Bridge for IDE Plugin Communication
 *
 * Auto-detects platform (VSCode / Eclipse / Browser).
 * In browser mode, all calls are no-ops — zero overhead for web users.
 */

import type { BridgeMessage, BridgeResponse, PluginPlatform, PluginShellKind } from './types'

class EventEmitter {
  private events: Map<string, Array<(...args: unknown[]) => void>> = new Map()

  on(event: string, listener: (...args: unknown[]) => void) {
    if (!this.events.has(event)) {
      this.events.set(event, [])
    }
    this.events.get(event)!.push(listener)
  }

  off(event: string, listener: (...args: unknown[]) => void) {
    const listeners = this.events.get(event)
    if (listeners) {
      const index = listeners.indexOf(listener)
      if (index !== -1) {
        listeners.splice(index, 1)
      }
    }
  }

  emit(event: string, ...args: unknown[]) {
    const listeners = this.events.get(event)
    if (listeners) {
      listeners.forEach(listener => listener(...args))
    }
  }
}

class UnifiedBridge extends EventEmitter {
  private platform: PluginPlatform
  private ready = false
  private pendingCalls: Array<() => void> = []
  private vscodeApi?: ReturnType<NonNullable<typeof window.acquireVsCodeApi>>
  private callbacks = new Map<
    string,
    { resolve: (value: unknown) => void; timeout: ReturnType<typeof setTimeout> }
  >()
  private counter = 0

  constructor() {
    super()
    this.platform = this.detectPlatform()
    this.initialize()
  }

  private refreshPlatformIfNeeded() {
    if (this.platform !== 'browser') {
      return
    }

    const detected = this.detectPlatform()
    if (detected !== 'browser') {
      this.platform = detected
      this.ready = false
      this.initialize()
    }
  }

  private detectPlatform(): PluginPlatform {
    if (typeof window.acquireVsCodeApi === 'function') return 'vscode'
    if (typeof window.eclipseCall === 'function' || window.eclipseReady) return 'eclipse'
    return 'browser'
  }

  private initialize() {
    if (this.platform === 'vscode') {
      this.initVSCode()
    } else if (this.platform === 'eclipse') {
      this.initEclipse()
    } else {
      this.ready = true
    }
  }

  private initVSCode(attempt = 0) {
    type VSCodeApiType = ReturnType<NonNullable<typeof window.acquireVsCodeApi>>
    const windowWithApi = window as unknown as { __vscodeApi?: VSCodeApiType }
    if (windowWithApi.__vscodeApi) {
      this.vscodeApi = windowWithApi.__vscodeApi
    } else if (typeof window.acquireVsCodeApi === 'function') {
      this.vscodeApi = window.acquireVsCodeApi()
      windowWithApi.__vscodeApi = this.vscodeApi
    }

    if (this.vscodeApi) {
      window.addEventListener('message', event => {
        const msg = event.data
        if (msg?.type === 'bridge-response') {
          const cb = this.callbacks.get(msg.id)
          if (cb) {
            clearTimeout(cb.timeout)
            this.callbacks.delete(msg.id)
            cb.resolve(msg.payload)
          }
        } else if (msg?.type === 'bridge-event') {
          this.handleMessage(msg.payload)
        }
      })

      // Keep bootstrap workspace paths in sync when IDE folders change
      this.on('workspaceFoldersChanged', (payload: unknown) => {
        const data = payload as { workspacePaths?: string[] } | undefined
        if (data?.workspacePaths && Array.isArray(data.workspacePaths)) {
          window.CURIETECH_WORKSPACE_PATHS = data.workspacePaths.filter(Boolean)
        }
      })

      this.ready = true
      this.processPending()
    } else if (attempt < 50) {
      setTimeout(() => this.initVSCode(attempt + 1), 100)
    } else {
      console.warn('[Bridge] VSCode API not available after 5s, falling back to browser mode')
      this.platform = 'browser'
      this.ready = true
      this.processPending()
    }
  }

  private initEclipse(attempt = 0) {
    if (window.eclipseReady || typeof window.eclipseCall !== 'undefined') {
      window.eclipseBridgeMessage = (message: unknown) => {
        try {
          const parsed = typeof message === 'string' ? JSON.parse(message) : message
          this.handleMessage(parsed)
        } catch (e) {
          console.error('[Bridge] Eclipse message error:', e)
        }
      }
      this.ready = true
      this.processPending()
    } else if (attempt < 50) {
      setTimeout(() => this.initEclipse(attempt + 1), 100)
    } else {
      console.warn('[Bridge] Eclipse API not available after 5s, falling back to browser mode')
      this.platform = 'browser'
      this.ready = true
      this.processPending()
    }
  }

  private processPending() {
    while (this.pendingCalls.length > 0) {
      this.pendingCalls.shift()?.()
    }
  }

  private handleMessage(message: unknown) {
    const msg = message as BridgeMessage
    if (msg.type === 'bridge-event' && msg.payload) {
      const p = msg.payload as { type?: string; payload?: unknown }
      if (p.type) {
        this.emit(p.type, p.payload)
        return
      }
    }
    this.emit(msg.type, msg.payload)
  }

  // ---- Core API ----

  async call<T = unknown>(
    namespace: string,
    operation: string,
    params: Record<string, unknown> = {}
  ): Promise<BridgeResponse<T>> {
    this.refreshPlatformIfNeeded()
    return new Promise(resolve => {
      const execute = async () => {
        try {
          if (this.platform === 'vscode') {
            resolve(await this.callVSCode(namespace, operation, params))
          } else if (this.platform === 'eclipse') {
            resolve(await this.callEclipse(namespace, operation, params))
          } else {
            resolve({ success: false, error: 'Not in plugin context' })
          }
        } catch (e) {
          resolve({ success: false, error: e instanceof Error ? e.message : 'Unknown error' })
        }
      }

      if (this.ready) execute()
      else this.pendingCalls.push(execute)
    })
  }

  private async callVSCode<T>(
    ns: string,
    op: string,
    params: Record<string, unknown>
  ): Promise<BridgeResponse<T>> {
    if (!this.vscodeApi) return { success: false, error: 'VSCode API unavailable' }

    const id = `b_${Date.now()}_${this.counter++}`
    return new Promise(resolve => {
      const timeout = setTimeout(() => {
        this.callbacks.delete(id)
        resolve({ success: false, error: 'Timeout' })
      }, 60000)

      this.callbacks.set(id, {
        resolve: v => resolve((typeof v === 'string' ? JSON.parse(v) : v) as BridgeResponse<T>),
        timeout
      })

      this.vscodeApi!.postMessage({
        type: 'bridge-call',
        id,
        method: ns,
        params: [op, params]
      })
    })
  }

  private async callEclipse<T>(
    ns: string,
    op: string,
    params: Record<string, unknown>
  ): Promise<BridgeResponse<T>> {
    if (typeof window.eclipseCall !== 'function')
      return { success: false, error: 'Eclipse unavailable' }

    try {
      const result = window.eclipseCall(ns, op, JSON.stringify(params))
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        const resolved = await (result as Promise<unknown>)
        return (typeof resolved === 'string' ? JSON.parse(resolved) : resolved) as BridgeResponse<T>
      }
      return (typeof result === 'string' ? JSON.parse(result) : result) as BridgeResponse<T>
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Unknown error' }
    }
  }

  // ---- Environment ----

  getPlatform(): PluginPlatform {
    this.refreshPlatformIfNeeded()
    return this.platform
  }

  isInPlugin(): boolean {
    this.refreshPlatformIfNeeded()
    return (
      this.platform !== 'browser' ||
      typeof window.acquireVsCodeApi === 'function' ||
      typeof window.eclipseCall === 'function' ||
      Boolean(window.eclipseReady)
    )
  }

  /**
   * Distinguishes Anypoint Studio (Eclipse) from Anypoint Builder (VS Code) for plugin-specific UX.
   * Prefer `window.CURIETECH_PLUGIN_TYPE` when set by the shell; otherwise infer from bridge platform.
   */
  getPluginShellKind(): PluginShellKind {
    if (!this.isInPlugin()) {
      return 'unknown'
    }
    const t = window.CURIETECH_PLUGIN_TYPE
    if (t === 'anypoint-studio') {
      return 'studio'
    }
    if (t === 'anypoint-builder') {
      return 'builder'
    }
    const p = this.getPlatform()
    if (p === 'eclipse') {
      return 'studio'
    }
    if (p === 'vscode') {
      return 'builder'
    }
    return 'unknown'
  }

  getBaseUrl(): string {
    if (window.CURIETECH_BASE_URL) {
      return window.CURIETECH_BASE_URL
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
      const origin = window.location.origin
      if (!origin.startsWith('vscode-webview://')) {
        return origin
      }
    }
    return 'http://localhost:3000'
  }

  // ---- Convenience methods for plugin features ----

  async openInBrowser(url: string): Promise<BridgeResponse<void>> {
    return this.call('ui', 'openExternal', { url })
  }

  async saveFile(content: string, filename: string): Promise<BridgeResponse<{ path: string }>> {
    const result = await this.call<{ path: string }>('ui', 'saveFile', { content, filename })
    if (!result.success && result.error?.includes('Unknown ui operation')) {
      return { success: false, error: 'unsupported' }
    }
    return result
  }

  async pickFolder(title?: string): Promise<BridgeResponse<{ path: string; name: string }>> {
    const result = await this.call<{ path: string; name: string }>(
      'ui',
      'pickFolder',
      title ? { title } : {}
    )
    if (!result.success && result.error?.includes('Unknown ui operation')) {
      return { success: false, error: 'unsupported' }
    }
    return result
  }

  async applyCodeChange(change: unknown): Promise<
    BridgeResponse<{
      status?: string
      results?: Array<{ path: string; success: boolean; error?: string }>
    }>
  > {
    const changeObj = change as { rootPath?: string; diffs?: Array<Record<string, unknown>> }
    if (!changeObj?.diffs?.length) {
      return { success: false, error: 'No diffs provided' }
    }

    const rootPath = changeObj.rootPath ?? ''
    const results: Array<{ path: string; success: boolean; error?: string }> = []
    const writeBatch: Array<{ path: string; content: string }> = []
    const deletePaths: string[] = []

    for (const diff of changeObj.diffs) {
      const diffType = String(diff.diffType ?? 'MODIFY')
      const bFilePath = diff.bFilePath ? String(diff.bFilePath) : null
      const aFilePath = diff.aFilePath ? String(diff.aFilePath) : null
      const bFileContent = diff.bFileContent != null ? String(diff.bFileContent) : null
      const targetPath = diffType === 'DELETE' ? aFilePath : bFilePath

      if (!targetPath) {
        results.push({ path: '', success: false, error: `Missing file path for ${diffType}` })
        continue
      }

      const resolvedPath = targetPath.startsWith('/')
        ? targetPath
        : rootPath
          ? `${rootPath}/${targetPath}`
          : targetPath

      if (diffType === 'DELETE') {
        deletePaths.push(resolvedPath)
      } else if (bFileContent != null) {
        writeBatch.push({ path: resolvedPath, content: bFileContent })
      } else {
        results.push({ path: targetPath, success: false, error: 'Missing bFileContent' })
      }
    }

    if (writeBatch.length > 0) {
      const writeResult = await this.call<{
        results: Array<{ path: string; success: boolean; error?: string }>
      }>('fs', 'writeMany', { files: writeBatch })
      if (writeResult.success && writeResult.data?.results) {
        results.push(...writeResult.data.results)
      }
    }

    for (const p of deletePaths) {
      const delResult = await this.call('fs', 'delete', { path: p })
      results.push({ path: p, success: delResult.success, error: delResult.error })
    }

    const allSuccess = results.every(r => r.success)
    return { success: true, data: { results, status: allSuccess ? 'applied' : 'partial' } }
  }

  async importProject(
    projectPath: string,
    projectName: string
  ): Promise<BridgeResponse<{ alreadyExists?: boolean }>> {
    return this.call('workspace', 'importProject', { path: projectPath, name: projectName })
  }

  async removeProject(projectPath: string, projectName: string): Promise<BridgeResponse<void>> {
    return this.call('workspace', 'removeProject', { path: projectPath, name: projectName })
  }

  async getWorkspaceInfo(): Promise<
    BridgeResponse<{
      rootPath: string
      name: string
      projects?: Array<{ name: string; path?: string }>
    }>
  > {
    const metaResult = await this.call<Record<string, unknown>>('meta', 'getInfo', {})

    const getNameFromPath = (path?: string): string =>
      path?.split(/[\\/]/).filter(Boolean).pop() || 'Workspace'

    const toProject = (item: {
      name?: string
      path?: string
    }): { name: string; path?: string } => ({
      name: item.name || getNameFromPath(item.path),
      path: item.path
    })

    if (metaResult.success && metaResult.data) {
      const raw = metaResult.data

      // Primary shape: plural fields. Legacy singular is mapped once for compatibility.
      const workspacePaths = Array.isArray(raw.workspacePaths)
        ? raw.workspacePaths.filter(
            (path): path is string => typeof path === 'string' && Boolean(path)
          )
        : []
      if (
        workspacePaths.length === 0 &&
        typeof raw.workspacePath === 'string' &&
        raw.workspacePath
      ) {
        workspacePaths.push(raw.workspacePath)
      }

      const projects = Array.isArray(raw.projects)
        ? raw.projects
            .map(project =>
              project && typeof project === 'object'
                ? toProject(project as { name?: string; path?: string })
                : null
            )
            .filter((project): project is { name: string; path?: string } => Boolean(project))
        : []
      const workspaceFolders = Array.isArray(raw.workspaceFolders)
        ? raw.workspaceFolders
            .map(folder =>
              folder && typeof folder === 'object'
                ? toProject(folder as { name?: string; path?: string })
                : null
            )
            .filter((folder): folder is { name: string; path?: string } => Boolean(folder))
        : []
      const pathProjects = workspacePaths.map(path => toProject({ path }))

      const dedupedByPath = new Map<string, { name: string; path?: string }>()
      for (const project of [...projects, ...workspaceFolders, ...pathProjects]) {
        if (project.path) {
          dedupedByPath.set(project.path, project)
        }
      }
      const normalizedProjects = Array.from(dedupedByPath.values())

      return {
        success: true,
        data: {
          rootPath: workspacePaths[0] || normalizedProjects[0]?.path || '',
          name: 'Workspace',
          projects: normalizedProjects
        }
      }
    }

    return { success: true, data: { rootPath: '', name: 'Workspace', projects: [] } }
  }

  /**
   * Thin-shell semver from the IDE plugin (Studio / Builder), for /api/version checks.
   * Prefer bridge meta; Eclipse may also set window.CURIETECH_SHELL_VERSION before bridge init.
   */
  async getShellVersion(): Promise<string | null> {
    if (!this.isInPlugin()) {
      return null
    }

    const fromWindow = ((): string | null => {
      const w = window as unknown as { CURIETECH_SHELL_VERSION?: string }
      return typeof w.CURIETECH_SHELL_VERSION === 'string' && w.CURIETECH_SHELL_VERSION.trim()
        ? w.CURIETECH_SHELL_VERSION.trim()
        : null
    })()
    if (fromWindow) {
      return fromWindow
    }

    for (let attempt = 0; attempt < 15; attempt++) {
      const metaResult = await this.call<Record<string, unknown>>('meta', 'getInfo', {})
      if (metaResult.success && metaResult.data) {
        const v = metaResult.data.shellVersion
        if (typeof v === 'string' && v.trim()) {
          return v.trim()
        }
      }
      await new Promise(r => setTimeout(r, 80))
    }

    return null
  }

  // ---- Runner (uses generic process primitives + local state) ----

  private _runnerProcessId: string | null = null
  private _installationState: 'idle' | 'installing' | 'completed' | 'failed' = 'idle'
  private _installationStep: string | undefined = undefined
  private _installationError: string | undefined = undefined
  private _cleaningUp = false

  async startRunner(
    installCommand: string
  ): Promise<BridgeResponse<{ status: string; pid?: number; message?: string }>> {
    if (this.platform === 'eclipse') {
      return this.call('runner', 'start', { installCommand })
    }

    try {
      const result = await this.call<{ id: string; pid: number }>('process', 'spawn', {
        command: installCommand,
        label: 'curie-runner'
      })
      if (!result.success || !result.data) {
        return { success: false, error: result.error ?? 'Failed to spawn runner process' }
      }
      this._runnerProcessId = result.data.id
      this._installationState = 'installing'
      this._installationStep = 'Starting installation...'
      this._installationError = undefined
      return { success: true, data: { status: 'installing', pid: result.data.pid } }
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Unknown error' }
    }
  }

  async stopRunner(): Promise<BridgeResponse<void>> {
    if (this.platform === 'eclipse') {
      return this.call('runner', 'stop', {})
    }

    if (!this._runnerProcessId) {
      return { success: true }
    }
    this._cleaningUp = true
    try {
      const result = await this.call('process', 'kill', { id: this._runnerProcessId })
      if (!result.success) {
        this._cleaningUp = false
        return result as BridgeResponse<void>
      }

      // Poll until process exits, then clean up (await so callers get accurate completion)
      const processId = this._runnerProcessId
      for (let i = 0; i < 60; i++) {
        const status = await this.call<{
          processes: Array<{ running: boolean }>
        }>('process', 'status', { id: processId })
        if (status.success && status.data?.processes[0] && !status.data.processes[0].running) {
          break
        }
        await new Promise(r => setTimeout(r, 500))
      }
      this._runnerProcessId = null
      this._installationState = 'idle'
      this._installationStep = undefined
      this._installationError = undefined
      this._cleaningUp = false

      return { success: true }
    } catch (e) {
      this._cleaningUp = false
      return { success: false, error: e instanceof Error ? e.message : 'Unknown error' }
    }
  }

  async getRunnerStatus(): Promise<
    BridgeResponse<{
      running: boolean
      pid?: number
      logsDir?: string
      cleaningUp?: boolean
      installationState?: 'idle' | 'installing' | 'completed' | 'failed'
      installationStep?: string
      installationError?: string
    }>
  > {
    if (this.platform === 'eclipse') {
      return this.call('runner', 'getStatus', {})
    }

    if (this.platform === 'browser') {
      return {
        success: true,
        data: { running: false, cleaningUp: false, installationState: 'idle' }
      }
    }

    // Try to reconnect to an existing runner process after webview restart
    if (!this._runnerProcessId) {
      const all = await this.call<{
        processes: Array<{
          id: string
          pid: number
          running: boolean
          exitCode: number | null
          label?: string
        }>
      }>('process', 'status', {})
      if (all.success && all.data?.processes) {
        const runner = all.data.processes.find(p => p.label === 'curie-runner')
        if (runner) {
          this._runnerProcessId = runner.id
          if (runner.running) {
            this._installationState = 'installing'
          } else if (runner.exitCode === 0) {
            this._installationState = 'completed'
          } else if (runner.exitCode !== null) {
            this._installationState = 'failed'
          }
        }
      }
    }

    if (!this._runnerProcessId) {
      return {
        success: true,
        data: {
          running: false,
          cleaningUp: this._cleaningUp,
          installationState: this._installationState
        }
      }
    }

    const status = await this.call<{
      processes: Array<{
        id: string
        pid: number
        running: boolean
        exitCode: number | null
        label?: string
      }>
    }>('process', 'status', { id: this._runnerProcessId })

    if (!status.success || !status.data?.processes[0]) {
      return {
        success: true,
        data: {
          running: false,
          cleaningUp: this._cleaningUp,
          installationState: this._installationState,
          installationError: this._installationError
        }
      }
    }

    const proc = status.data.processes[0]

    // Read recent output for step tracking
    const output = await this.call<{ lines: string[] }>('process', 'output', {
      id: this._runnerProcessId,
      tail: 10
    })
    const lines = output.success && output.data ? output.data.lines : []
    const lastLine = [...lines].reverse().find(l => l.trim().length > 0)

    // Update installation state based on process status
    if (proc.running) {
      this._installationState = 'installing'
      if (lastLine) this._installationStep = lastLine
    } else if (proc.exitCode === 0) {
      this._installationState = 'completed'
      this._installationStep = undefined
    } else if (proc.exitCode !== null) {
      this._installationState = 'failed'
      this._installationError = lastLine ?? `Process exited with code ${proc.exitCode}`
    }

    return {
      success: true,
      data: {
        running: proc.running,
        pid: proc.pid,
        cleaningUp: this._cleaningUp,
        installationState: this._installationState,
        installationStep: this._installationStep,
        installationError: this._installationError
      }
    }
  }

  async resetInstallationState(): Promise<BridgeResponse<void>> {
    if (this.platform === 'eclipse') {
      return this.call('runner', 'resetInstallationState', {})
    }
    this._installationState = 'idle'
    this._installationStep = undefined
    this._installationError = undefined
    return { success: true }
  }

  async cancelInstallation(): Promise<BridgeResponse<void>> {
    if (this.platform === 'eclipse') {
      return this.call('runner', 'cancelInstallation', {})
    }
    if (this._runnerProcessId) {
      await this.call('process', 'kill', { id: this._runnerProcessId })
      this._runnerProcessId = null
    }
    this._installationState = 'idle'
    this._installationStep = undefined
    this._installationError = undefined
    return { success: true }
  }

  async log(level: string, message: string): Promise<void> {
    if (this.platform === 'browser') return
    await this.call('meta', 'log', { level, message })
  }

  async selectWorkspace(_workspace: { uuid: string; name: string }): Promise<BridgeResponse<void>> {
    return { success: true }
  }

  async getCurrentWorkspace(): Promise<BridgeResponse<{ uuid?: string; name?: string }>> {
    return { success: true, data: undefined }
  }
}

export const bridge = new UnifiedBridge()

export function initBridge() {
  if (typeof window.eclipseCall !== 'undefined') {
    window.eclipseReady = true
  }

  // In plugin mode, intercept window.open to route external URLs through the bridge
  if (bridge.isInPlugin()) {
    const originalOpen = window.open.bind(window)
    window.open = (url?: string | URL, target?: string, features?: string): WindowProxy | null => {
      if (url && (target === '_blank' || !target)) {
        const urlStr = typeof url === 'string' ? url : url.toString()
        if (
          urlStr.startsWith('http://') ||
          urlStr.startsWith('https://') ||
          urlStr.startsWith('mailto:')
        ) {
          bridge.openInBrowser(urlStr)
          return null
        }
      }
      return originalOpen(url, target, features)
    }
  }
}

export type { BridgeResponse, PluginPlatform } from './types'
